import type { MouseEvent, ReactElement } from 'react';
import { useState } from 'react';

import type { IconProps } from '@bestseller-bit/frontend-community.theme.buying-theme';

import { MoreIcon } from '@bestseller-bit/frontend-community.theme.buying-theme';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import Menu, { type MenuProps } from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';

interface ActionConfig {
    label: string;
    icon: ReactElement;
    disabled?: boolean;
    onClick: (e: MouseEvent<HTMLButtonElement | HTMLLIElement>) => void;
}

interface ActionButtonProps {
    actions: Array<ActionConfig>;
    iconButtonProps?: Partial<IconButtonProps>;
    iconProps?: Partial<IconProps>;
    menuProps?: Partial<MenuProps>;
    /**
     * When true, the button shows a visual focus outline even though it does not hold real DOM focus.
     * This is driven by the focus proxy pattern in the BOM table: a screen-reader-only proxy element
     * in each table row signals focus state to this spanning button so users see feedback from
     * every row's tab stop.
     * When provided (even as false), the button also receives `tabIndex={-1}` to avoid duplicate
     * tab stops — the proxy handles the tab stop for each row instead.
     */
    isFocused?: boolean;
}

/**
 * This component expects an array of actions to be passed in as props.
 * If only one of these actions is enabled (either the rest is disabled or there is only 1 action in total), it will be rendered as a button.
 * If there are multiple actions, it will be rendered as a tripple dot icon button and function as a dropdown menu.
 */
const ActionButton = ({
    actions,
    iconButtonProps,
    iconProps,
    menuProps,
    isFocused,
}: ActionButtonProps): ReactElement => {
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

    const showMenu = Boolean(anchorEl);

    const enabledActions = actions.filter((action) => !action.disabled);

    const handleClick = (event: MouseEvent<HTMLButtonElement>): void => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = (): void => {
        setAnchorEl(null);
    };

    // Show focus outline when proxy signals focus
    const focusSx = isFocused
        ? {
              outline: '2px solid #1976d2',
              outlineOffset: '2px',
          }
        : {};

    // If no actions are provided, and therefore we have no enabled actions, throw an error
    if (actions.length === 0) {
        throw new Error(
            'No actions provided. Please check the actions prop and ensure there is at least one action.'
        );
    }

    // If no actions are enabled, return null
    if (!enabledActions.length) {
        return <span />;
    }

    // If we only have one enabled action, render that as the button
    if (enabledActions.length === 1) {
        const action = enabledActions[0];
        return (
            <IconButton
                variant="text"
                // When isFocused is provided, the button is controlled by a proxy and should not
                // be directly tabbable — the proxy handles the tab stop for each row.
                tabIndex={isFocused !== undefined ? -1 : undefined}
                {...iconButtonProps}
                sx={{
                    ...(iconButtonProps?.sx as Record<string, unknown>),
                    ...focusSx,
                }}
                // eslint-disable-next-line react/jsx-handler-names
                onClick={action.onClick}
            >
                {action.icon}
            </IconButton>
        );
    }

    // If we have more than one enabled actions, or if all are disabled, render them as a dropdown menu
    return (
        <>
            <IconButton
                variant="text"
                /**
                 * When isFocused is provided, the button is controlled by a proxy and should not
                 * be directly tabbable — the proxy handles the tab stop for each row.
                 */
                tabIndex={isFocused !== undefined ? -1 : undefined}
                {...iconButtonProps}
                sx={{
                    ...(iconButtonProps?.sx as Record<string, unknown>),
                    ...focusSx,
                }}
                onClick={handleClick}
            >
                <MoreIcon {...iconProps} />
            </IconButton>
            <Menu
                id="actions-menu-more-dropdown"
                anchorEl={anchorEl}
                open={showMenu}
                // Ensures the menu is allowed to touch the edge of the screen
                marginThreshold={0}
                onClose={handleClose}
                {...menuProps}
                slotProps={{
                    ...menuProps?.slotProps,

                    paper: {
                        // eslint-disable-next-line @typescript-eslint/no-misused-spread
                        ...menuProps?.slotProps?.paper,
                        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
                        sx: {
                            // @ts-expect-error - There is no type safety for the sx prop on paper
                            ...menuProps?.slotProps?.paper?.sx,
                            boxShadow: ({ shadows }) => shadows[2],
                        },
                    },

                    list: {
                        ['aria-labelledby']: 'basic-button',
                        // eslint-disable-next-line @typescript-eslint/no-misused-spread
                        ...menuProps?.slotProps?.list,
                    },
                }}
            >
                {enabledActions.map((action) => (
                    <MenuItem
                        key={`action-menu-item-${action.label}-${String(action.icon)}`}
                        sx={{
                            padding: ({ spacing }) => spacing(3, 4),
                        }}
                        onClick={(e) => {
                            handleClose(); // 1. Close the dropdown
                            setTimeout(() => {
                                // 2. Run the action after the menu is closed
                                action.onClick(e);
                            }, 0);
                        }}
                    >
                        <Stack
                            direction="row"
                            spacing={2}
                            sx={{
                                alignItems: 'center',
                            }}
                        >
                            {action.icon}
                            <Typography variant="buttons.xsmall">
                                {action.label}
                            </Typography>
                        </Stack>
                    </MenuItem>
                ))}
            </Menu>
        </>
    );
};

export { ActionButton };
export type { ActionConfig };
